import { useEffect, useMemo, useRef } from 'react';
import { ConnectDragSource, useDrag } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { DragSourceType } from 'const';
import { toggleDragState } from 'containers/App/actions';
import { ArtboardLayoutDragObject, CombinedDocumentsMap, LayoutMap } from 'models';
import { isReusableLayout } from 'utils/reusableLayouts/isReusableLayout';

type DragSourceHook = {
  connectDragSource: ConnectDragSource;
};

type Props = {
  groupLayoutId: string | undefined;
  layout: LayoutMap;
  documents: CombinedDocumentsMap | undefined;
  canDrag?: boolean;
};

export const useArtboardLayoutContainerDragSource = ({ groupLayoutId, layout, documents, canDrag = false }: Props): DragSourceHook => {
  const dispatch = useDispatch();
  const dragSourceType = groupLayoutId
    ? DragSourceType.GROUP_LAYOUT_REUSABLE_LAYOUT
    : documents && isReusableLayout(layout, documents)
      ? DragSourceType.ARTBOARD_REUSABLE_LAYOUT
      : DragSourceType.ARTBOARD_LAYOUT;

  // Track canDrag in a ref so we don't recreate the drag spec (and re-register) on every flag flip.
  const canDragRef = useRef<boolean>(canDrag);
  useEffect(() => {
    canDragRef.current = canDrag;
  }, [canDrag]);

  // Memoize spec so registration doesn't churn when only canDrag changes.
  const dragSpec = useMemo(() => ({
    type: dragSourceType,
    canDrag: () => canDragRef.current,
    item: (): ArtboardLayoutDragObject => {
      // Defer global drag state toggle to next frame to avoid layout thrash causing premature dragend.
      requestAnimationFrame(() => dispatch(toggleDragState(dragSourceType)));

      return { layout } as ArtboardLayoutDragObject;
    },
    end: (): void => {
      // Symmetric deferral; helps if drop triggers heavy re-render.
      requestAnimationFrame(() => dispatch(toggleDragState()));
    },
  }), [dispatch, dragSourceType, layout]);

  const [, connectDragSource] = useDrag<ArtboardLayoutDragObject, void, void>(dragSpec);

  return {
    connectDragSource,
  };
};
